<template>
  <div>
    <div v-if="!user && !tempUser">
      <p class="fs-12 text-center">
        {{ $t('lps.We work hard to let only real people enter our platform.') }}
      </p>
      <div
        class="step-title"
        style="z-index: 2; position: relative;"
      >
        {{ $t('lps.Write email to select your partner') }}
      </div>
      <validation-observer
        ref="loginForm"
        #default="{invalid}"
      >
        <b-form
          class="auth-login-form mt-2"
          @submit.prevent="emailCheck"
        >
          <b-form-group
            label-for="login-phone"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Email')"
              vid="email"
              rules="required|email"
              data-vv-validate-on="blur"
            >
              <b-form-input
                id="email"
                v-model="email"
                name="email"
                class="placeholder-black"
                :placeholder="$t('Email')"
                :state="errors.length > 0 ? false:null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <input
            id="emailInfoToggle"
            type="checkbox"
            class="expandable-toggle"
          >
          <div class="expandable">
            <label
              for="emailInfoToggle"
              class="expandable-header mb-0"
            >
              <span class="fs-14 font-weight-bold">
                {{ $t('lps.Why do we ask for your email?') }}
              </span>
              <span class="arrow">
                <feather-icon
                  icon="ChevronDownIcon"
                  size="22"
                  stroke="#333"
                />
              </span>
            </label>
            <div class="expandable-content">
              <p
                class="fs-11"
                style="font-weight: 300"
              >
                {{ $t('lps.We ask for your email to ensure account security, provide important updates, and offer personalized support, all while respecting your privacy and keeping your information safe.') }}
              </p>
            </div>
          </div>
          <b-button
            class="mt-2"
            type="submit"
            variant="primary"
            :style="nextButtonStyle"
            block
            :disabled="loading || invalid || !termCheck1 || !termCheck2"
          >
            <b-spinner
              v-if="loading"
              small
            />
            {{ nextButtonText }}
          </b-button>
        </b-form>
      </validation-observer>
      <p class="or-sign-up">
        — {{ $t('or Sign up with') }} —
      </p>
      <div class="social-buttons">
        <div
          class="apple-login-button social-button"
          style="padding: 14px"
          :class="{'disable-button': !enableGoogleAuth || !termCheck1 || !termCheck2}"
          @click="enableGoogleAuth && termCheck1 && termCheck2 ? loginWithGoogle() : showAcceptTerms()"
        >
          <b-img
            :src="iconGoogle"
            class="mr-05"
          />
          {{ $t('auth.socials.Connect via Google') }}
        </div>
        <div
          class="apple-login-button social-button"
          style="padding: 14px"
          :class="{'disable-button': !termCheck1 || !termCheck2}"
          @click="termCheck1 && termCheck2 ? loginWithApple() : showAcceptTerms()"
        >
          <b-img
            :src="iconApple"
            class="mr-05"
          />
          {{ $t('auth.socials.Connect via Apple') }}
        </div>
      </div>
    </div>

    <div v-if="!user && tempUser && step === 6">
      <div class="step-title">
        {{ $t('lps.Enter your pin to meet users!') }}
      </div>
      <!-- form -->
      <validation-observer
        ref="form"
        #default="{invalid}"
      >
        <b-form
          class="auth-login-form"
          @submit.prevent="loginOrRegister"
        >
          <!-- forgot password -->
          <b-form-group>
            <b-row class="password-information">
              <b-col
                sm="12"
                class="mt-2"
              >
                <div class="d-flex justify-content-center">
                  <validation-provider
                    #default="{ errors }"
                    :name="$t('Password')"
                    vid="password"
                  >
                    <PincodeInput v-model="password" />
                    <br>
                    <div class="text-danger text-center mt-2">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </div>
              </b-col>

              <b-col
                v-if="(authType === 'email') || authType !== 'email'"
                sm="12"
              >
                <p class="send-again cursor-pointer text-center">
                  <span
                    class="link text-underline"
                    :class="{'passive-link': !sendAgainable}"
                    @click="sendAgainable ? sendAgain() : null"
                  >
                    {{ userExists ? $t('auth.password.Forgot Pin Code') : $t('Resend') }}
                  </span>
                </p>
              </b-col>
            </b-row>
          </b-form-group>

          <!-- submit buttons -->
          <b-button
            class="mt-2"
            type="submit"
            variant="primary"
            block
            :style="nextButtonStyle"
            :disabled="loading || invalid"
          >
            <b-spinner
              v-if="loading"
              small
            />
            {{ nextButtonText }}
          </b-button>
        </b-form>
      </validation-observer>
    </div>

    <div v-if="user">
      <div class="step-title">
        {{ $t('lps.No matches anymore!') }}
      </div>
      <p class="text-center">
        {{ $t('lps.With Boho, you can make live video calls, audio calls and chat with random people.') }}
      </p>
      <p class="text-center">
        {{ $t('lps.Find friends for chat & voice call for conversation.') }}
      </p>
      <p class="text-center">
        {{ $t('lps.Whether you\'re looking to make new friends or find someone special Boho is the place to go.') }}
      </p>
      <b-button
        class="mt-2"
        variant="primary"
        :style="nextButtonStyle"
        block
        @click="$emit('nextStep')"
      >
        {{ $t('lps.Discover Profiles') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import {
  clarityPushUserId,
  gtmPushUserId,
  gtmTrackNewEvent,
  showNotify,
  throwDefaultError,
} from '@/helpers/helpers'
import {
  BButton,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BImg,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import PincodeInput from 'vue-pincode-input'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
// eslint-disable-next-line import/no-extraneous-dependencies
// eslint-disable-next-line import/no-extraneous-dependencies
import 'vue-scroll-picker/dist/style.css'
import moment from 'moment'

const appleClientId = process.env.VUE_APP_APPLE_CLIENT_ID || 'com.arasana.web'
const appUrl = process.env.VUE_APP_URL

const iconApple = require('@/assets/images/icons/apple.svg')
const iconGoogle = require('@/assets/images/lps/google.png')

const defaultAbility = [
  {
    action: 'manage',
    subject: 'all',
  },
  {
    action: 'read',
    subject: 'all',
  },
]

export default {
  name: 'LpAuth3',
  components: {
    BButton,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BImg,
    BRow,
    BSpinner,
    PincodeInput,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    age: {
      type: Number,
      default: 18,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    showAuth: {
      type: Boolean,
      required: false,
    },
    sendMainPageAfterAuth: {
      type: Boolean,
      required: false,
    },
    step: {
      type: Number,
      default: 1,
    },
    // eslint-disable-next-line vue/require-prop-types
    tempUser: {
      default: null,
    },
    username: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      dates: [
        '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19',
        '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', '30', '31',
      ],
      email: '',
      enableGoogleAuth: false,
      enableGoogleAuthInterval: null,
      enableGoogleAuthIntervalTime: 100,
      iconApple,
      iconGoogle,
      modalPassword: false,
      months: [
        '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12',
      ],
      password: '',
      sendAgainable: true,
      sendAgainableTime: 120,
      termCheck1: false,
      termCheck2: false,
      termDidNotAccepted: false,
      userExists: false,
    }
  },
  computed: {
    authType() {
      return this.$store.getters['auth/authType']
    },
    nextButtonStyle() {
      return 'background-color: #AC33F4 !important; color: #fff !important; padding: 16px; border-radius: 16px;'
    },
    nextButtonText() {
      return this.$t('Next')
    },
    socialRegistering() {
      return this.$store.getters['auth/socialRegistering']
    },
    user() {
      return this.$store.getters['auth/user']
    },
    years() {
      // eslint-disable-next-line no-underscore-dangle
      const _years = []
      const lastYear = new Date().getFullYear() - 18

      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < 100; i++) {
        _years.push((lastYear - i).toString())
      }

      return _years
    },
  },
  watch: {
    password(newValue, oldValue) {
      if (oldValue.length === 3 && newValue.length === 4) {
        this.loginOrRegister()
      }
    },
  },
  created() {
    // eslint-disable-next-line no-undef
    AppleID.auth.init({
      clientId: appleClientId,
      scope: 'name email',
      redirectURI: `${appUrl}/login`,
      state: new Date().getTime().toString(),
      usePopup: true,
    })

    this.enableGoogleAuthInterval = setInterval(() => {
      if (this.$gAuth.isLoaded()) {
        clearInterval(this.enableGoogleAuthInterval)
        this.enableGoogleAuth = true
      }
    }, this.enableGoogleAuthIntervalTime)

    this.termCheck1 = true
    this.termCheck2 = true
  },
  methods: {
    emailCheck() {
      this.$refs.loginForm.validate().then(success => {
        if (success) {
          this.$emit('setLoading', true)
          this.$emit('updateEmail', this.email)
          useJwt.emailVerification({ email: this.email, lang: this.$i18n.locale })
            .then(() => {
              this.$store.dispatch('auth/setAuthType', 'email')
              // this.modalPersonalInformation = true
              // this.personalInformationFormSubmitted()
              this.$emit('setLoading', false)
              this.$emit('updateTempUser', { email: this.email })
              this.$emit('nextStep')
            })
            .catch(error => {
              // eslint-disable-next-line eqeqeq
              if (error?.response?.data?.header?.error?.errorCode == '370') {
                showNotify(this.$toast, this.$t('Please register with a new account. You cannot log in with your existing account because you have purchased a promotional product.'), 'danger')
              } else {
                this.$emit('setLoading', false)
                throwDefaultError(this.$toast, error)
              }
            })
            .finally(() => {
              this.$emit('setLoading', false)
            })
        }
      })
    },
    letIn(response) {
      useJwt.setToken(response.data.body.loginToken.token)
      this.$store.dispatch('auth/setToken', response.data.body.loginToken.token)
      this.$store.dispatch('auth/updateUser', {
        ability: defaultAbility,
        ...response.data.body,
      })
      this.$emit('lpUserAction', 'authenticated')
      clarityPushUserId(response.data.body.id)
      gtmPushUserId(response.data.body.id)
      gtmTrackNewEvent('login', {
        category: 'Login Funnel',
        action: 'Login',
      })
      this.$ability.update(defaultAbility)

      this.modalPassword = false
      this.password = ''

      this.userExists = response.data.body.userExists

      if (response.data.body.userExists || this.sendMainPageAfterAuth) {
        this.$emit('lpUserAction', 'current-user-detected')
        this.$router.push('/')
      } else {
        this.$emit('setLoading', false)
      }
    },
    // eslint-disable-next-line consistent-return
    login() {
      if (this.password.length < 4) {
        this.$refs.form.setErrors({
          password: this.$t('{field} is required', { field: this.$t('Password') }),
        })
        return false
      }
      // Email mi telefon mu
      if (this.authType === 'email') {
        if (this.tempUser.email && this.tempUser.email !== '') {
          this.$emit('setLoading', true)
          useJwt.emailLogin({
            email: this.tempUser.email,
            pinCode: this.password,
            lang: this.$i18n.locale,
            deviceId: this.$store.getters['app/deviceId'],
          })
            .then(response => {
              this.letIn(response)
            })
            .catch(error => {
              throwDefaultError(this.$toast, error)
            })
            .finally(() => {
              this.password = ''
              this.$emit('setLoading', false)
            })
        }
      } else if (this.phone && this.phone !== '') {
        this.$emit('setLoading', true)
        useJwt.login({ phoneNumber: this.phone, password: this.password, lang: this.$i18n.locale })
          .then(response => {
            this.letIn(response)
          })
          .catch(error => {
            throwDefaultError(this.$toast, error)
          })
          .finally(() => {
            this.password = ''
            this.$emit('setLoading', false)
          })
      }
    },
    loginOrRegister() {
      if (this.tempUser) {
        this.register()
      } else {
        this.login()
      }
    },
    loginWithGoogle() {
      this.$emit('setLoading', true)
      this.$gAuth
        .signIn()
        .then(GoogleUser => {
          const userInfo = {
            accessToken: GoogleUser.getAuthResponse().id_token,
            userId: GoogleUser.getId(),
            lang: this.$i18n.locale,
            deviceId: this.$store.getters['app/deviceId'],
          }

          useJwt
            .loginWithGoogle(userInfo)
            .then(response => {
              const data = response.data.body
              if (data.registrationStatus === 'pending') {
                this.$store.dispatch('auth/setGoogleUser', data)
                this.$store.dispatch('auth/setTempUser', data)
                this.$store.dispatch('auth/setSocialRegistering', true)
                // this.modalPersonalInformation = true
                // this.personalInformationFormSubmitted()
                this.$emit('updateTempUser', data)
                this.$emit('nextStep')
              } else {
                // this.$store.dispatch('auth/setGoogleUser', null)
                // this.$store.dispatch('auth/setSocialRegistering', false)
                // useJwt.setToken(data.loginToken.token)
                // this.$store.dispatch('auth/setToken', data.loginToken.token)
                // this.$store.dispatch('auth/updateUser', {
                //   ability: defaultAbility,
                //   ...data,
                // })
                // this.$emit('lpUserAction', 'authenticated')
                // this.$ability.update(defaultAbility)
                //
                // if (data.userExists || this.sendMainPageAfterAuth) {
                //   this.$emit('lpUserAction', 'current-user-detected')
                //   this.$router.push('/')
                // }
                showNotify(this.$toast, this.$t('Please register with a new account. You cannot log in with your existing account because you have purchased a promotional product.'), 'danger')
              }
            })
            .catch(error => {
              if (error?.response?.data?.header?.error?.errorCode === '396') {
                showNotify(this.$toast, this.$t('Please register with a new account. You cannot log in with your existing account because you have purchased a promotional product.'), 'danger')
              } else {
                throwDefaultError(this.$toast, error)
              }
            })
            .finally(() => {
              this.$emit('setLoading', false)
            })
        })
        .catch(error => {
          this.$emit('setLoading', false)
          throwDefaultError(this.$toast, error)
        })
    },
    async loginWithApple() {
      try {
        // eslint-disable-next-line no-undef
        const appleUserData = await AppleID.auth.signIn()

        // Handle successful response.
        if (typeof appleUserData.authorization.id_token === 'string') {
          const userInfo = {
            code: appleUserData.authorization.code,
            lang: this.$i18n.locale,
            deviceId: this.$store.getters['app/deviceId'],
          }

          this.$emit('setLoading', true)

          useJwt
            .loginWithApple(userInfo)
            .then(response => {
              const data = response.data.body
              if (data.registrationStatus === 'pending') {
                this.$store.dispatch('auth/setAppleUser', data)
                this.$store.dispatch('auth/setTempUser', data)
                this.$store.dispatch('auth/setSocialRegistering', true)
                // this.modalPersonalInformation = true
                // this.personalInformationFormSubmitted()
                this.$emit('updateTempUser', data)
                this.$emit('nextStep')
              } else {
                // this.$store.dispatch('auth/setAppleUser', null)
                // this.$store.dispatch('auth/setSocialRegistering', false)
                // useJwt.setToken(data.loginToken.token)
                // this.$store.dispatch('auth/setToken', data.loginToken.token)
                // this.$store.dispatch('auth/updateUser', {
                //   ability: defaultAbility,
                //   ...data,
                // })
                // this.$emit('lpUserAction', 'authenticated')
                // this.$ability.update(defaultAbility)
                //
                // if (data.userExists || this.sendMainPageAfterAuth) {
                //   this.$emit('lpUserAction', 'current-user-detected')
                //   this.$router.push('/')
                // }
                showNotify(this.$toast, this.$t('Please register with a new account. You cannot log in with your existing account because you have purchased a promotional product.'), 'danger')
              }
            })
            .catch(error => {
              if (error?.response?.data?.header?.error?.errorCode === '396') {
                showNotify(this.$toast, this.$t('Please register with a new account. You cannot log in with your existing account because you have purchased a promotional product.'), 'danger')
              } else {
                throwDefaultError(this.$toast, error)
              }
            })
            .finally(() => {
              this.$emit('setLoading', false)
            })
        } else {
          // Email adresi yok uyarı göster
          showNotify(this.$toast, 'Please give permission to share your email address and try again', 'warning')
        }
      } catch (error) {
        throwDefaultError(this.$toast, error)
      }
    },
    personalInformationFormSubmitted() {
      if (this.socialRegistering) {
        // Profil bilgilerini güncelle
        const data = {
          userName: this.username,
          deviceId: this.$store.getters['app/deviceId'],
        }
        const profile = {
          gender: 'male',
          date: '01',
          month: '01',
          year: moment().subtract(this.age, 'years').year(),
        }

        this.$emit('setLoading', true)

        const { token } = this.tempUser.loginToken
        localStorage.setItem('tempLoginToken', token)

        const updateMe = useJwt.updateMe(data, token)
        const updateProfile = useJwt.updateProfile(profile, token)

        Promise.all([updateMe, updateProfile])
          .then(() => {
            useJwt.getMe(token)
              .then(response => {
                const user = response.data.body
                this.$store.dispatch('auth/setGoogleUser', null)
                this.$store.dispatch('auth/setSocialRegistering', false)
                useJwt.setToken(token)
                this.$store.dispatch('auth/setToken', token)
                useJwt.claimStoredReferral()
                this.$store.dispatch('auth/updateUser', {
                  ability: defaultAbility,
                  ...user,
                })
                this.$emit('lpUserAction', 'authenticated')
                this.$ability.update(defaultAbility)
                if (user.userExists || this.sendMainPageAfterAuth) {
                  this.$emit('lpUserAction', 'current-user-detected')
                  this.$router.push('/')
                }
              })
              .catch(error => {
                throwDefaultError(this.$toast, error)
              })
              .finally(() => {
                this.$emit('setLoading', false)
              })
          })
          .catch(error => {
            throwDefaultError(this.$toast, error)
            this.$emit('setLoading', false)
          })
          .finally(() => {
            this.$emit('setLoading', false)
          })
      } else {
        this.$store.dispatch('auth/updateTempUser2', {
          gender: 'male',
          lookingForGender: 'female',
          userName: this.username,
          date: '01',
          month: '01',
          year: moment().subtract(this.age, 'years').year(),
        })
        this.modalPassword = true
      }
    },
    // eslint-disable-next-line consistent-return
    register() {
      if (this.password.length < 4) {
        this.$refs.form.setErrors({
          password: this.$t('{field} is required', { field: this.$t('Password') }),
        })
        return false
      }
      this.$emit('setLoading', true)
      if (this.authType === 'email') {
        useJwt.emailRegister({
          email: this.tempUser.email,
          pinCode: this.password,
          lang: this.$i18n.locale,
          deviceId: this.$store.getters['app/deviceId'],
        })
          .then(async response => {
            // gtag('event', 'conversion', { send_to: 'AW-805828651/lOrECPO8oYIBEMPwn4AD', user: response.data.body })

            useJwt.setToken(response.data.body.loginToken.token)
            await this.$store.dispatch('auth/setToken', response.data.body.loginToken.token)

            const profile = {
              gender: 'male',
              lookingForGender: 'female',
              userName: this.username,
              date: '01',
              month: '01',
              year: moment().subtract(this.age, 'years').year(),
            }

            this.$emit('setLoading', true)

            useJwt.putPendingUser(profile)
              .then(async () => {
                useJwt.claimStoredReferral()
                this.$ability.update(defaultAbility)
                this.modalPassword = false
                this.$emit('setLoading', false)
                await this.$store.dispatch('auth/updateUser', {
                  ability: defaultAbility,
                  ...response.data.body,
                })
                this.$emit('lpUserAction', 'authenticated')
              })
              .catch(error => {
                throwDefaultError(this.$toast, error)
                this.$emit('setLoading', false)
                this.password = ''
              })
          })
          .catch(() => {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('You should check your login information and try again'),
                variant: 'danger',
              },
            })
            this.password = ''

            this.$emit('setLoading', false)
          })
      }
    },
    sendAgain() {
      this.$emit('setLoading', true)
      // Email mi telefon mu
      if (this.authType === 'email') {
        if (this.userExists) {
          useJwt.forgotPasswordEmail({ email: this.tempUser.email, lang: this.$i18n.locale })
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: this.$t('Password sent to email address.'),
                  variant: 'success',
                },
              })
              this.password = ''
              this.sendAgainable = false
              setInterval(() => {
                this.sendAgainable = true
              }, this.sendAgainableTime * 1000)
            })
            .catch(error => {
              throwDefaultError(this.$toast, error)
            })
            .finally(() => {
              this.$emit('setLoading', false)
            })
        } else {
          this.$emit('setLoading', true)
          useJwt.emailVerification({ email: this.tempUser.email, lang: this.$i18n.locale })
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: this.$t('Password sent to email address.'),
                  variant: 'success',
                },
              })
              this.password = ''
              this.sendAgainable = false
              setInterval(() => {
                this.sendAgainable = true
              }, this.sendAgainableTime * 1000)
            })
            .catch(error => {
              throwDefaultError(this.$toast, error)
            })
            .finally(() => {
              this.$emit('setLoading', false)
            })
        }
      } else {
        useJwt.forgotPassword(this.phone, this.$i18n.locale)
          .then(() => {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('Password sent to phone number.'),
                variant: 'success',
              },
            })
          })
          .catch(error => {
            throwDefaultError(this.$toast, error)
          })
          .finally(() => {
            this.$emit('setLoading', false)
          })
      }
    },
    showAcceptTerms() {
      this.termDidNotAccepted = true
    },
  },
}
</script>

<style lang="scss" scoped>
.auth-header {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  width: 100%;
  background: url('~@/assets/images/lps/lp-auth-header-bg.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}
.gender-select {
  display: flex;
  justify-content: space-between;
  width: 100%;

  .gender-button {
    width: 46%;
    padding: 10px;
    border: 1px solid #d3d3d5;
    border-radius: 5px;
    background-color: #ededef;
    cursor: pointer;
  }
  .gender-button.selected {
    background-color: #ce8ef3;
    border: 1px solid #AC33F4;
  }
}
.passive-link {
  opacity: 0.4 !important;
  cursor: not-allowed;
}
.expandable {
  width: 100%;
  background-color: #e3b5ff;
  border-radius: 18px;
  overflow: hidden;
  font-family: Arial, sans-serif;
  padding: 18px 16px;
}

.expandable-header {
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.expandable-header h2 {
  margin: 0;
  font-size: 18px;
  color: #4a148c;
}

.expandable-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
}

.expandable-content p {
  color: #4a148c;
  font-size: 14px;
  line-height: 1.5;
  margin-top: 18px;
  font-weight: normal;
}

.arrow {
  transition: transform 0.3s ease;
}

.expandable-toggle {
  display: none;
}

.expandable-toggle:checked + .expandable .arrow {
  transform: rotate(180deg);
}

.expandable-toggle:checked + .expandable .expandable-content {
  max-height: 300px; /* Adjust as needed */
}
</style>

<style lang="scss">
.auth-modal {
  .modal-body {
    padding: 0 !important;
  }
}
</style>
